import { ROLE } from './acl$';
import { BinaryTree } from './binary-tree';
import { BinaryTreeNode } from './binary-tree-node';
import { CoordinatorService } from './coordinator.service';
import { MemberCloudFile } from './member-cloud-file';
import { Project } from './project';
import { ProjectMember } from './project-member';
import { INTERVALARG } from './utils$';

export class ProjectTeam {
    isNewCloud = false;
    isReady = false;
    // private _isFirstCloudResult = true
    // private _firstTimeout: any

    private _memberMap = new Map<string, ProjectMember>();

    private _owner?: ProjectMember;
    private _user?: ProjectMember;

    constructor(
        public coords: CoordinatorService,
        public id: string,
    ) {}

    async init() {
        await this.coords.utils$.writeToDisk(`/${this.id}/.isCloudProject`, '');
        await this._listenCloud();
    }

    get cloudFileLs() {
        const ls: MemberCloudFile[] = [];
        Array.from(this._memberMap.values()).forEach((m) => ls.push(...m.cloudFileLs));
        return ls;
    }

    async deleteInvitationCode(code: string) {
        const resp = await this.coords.firebase$.callFunction('deleteInvitationCode', {
            code: code,
            projectId: this.id,
        });

        if (resp === 'ERROR') return false;
        return resp as any as boolean;
    }

    async createInvitationCode(
        code: string,
        role: ROLE,
        validity: INTERVALARG,
        canAttach?: boolean,
    ) {
        const tillDate = this.coords.utils$.futureDate(validity);

        const codeParams = {
            code: code,
            // userId: this.coords.user$.currentId,
            projectId: this.coords.project$.currentId,
            expiration: tillDate.toInstant().toString(),
            role: role,
            canAttach: canAttach,
        };
        const resp = await this.coords.firebase$.callFunction('createInvitationCode', codeParams);

        if (resp === 'ERROR') return false;

        return resp.data as string;
    }

    private async _listenCloud() {
        try {
            const uns = await this.coords.firebase$.firestore.listenQuery(
                'teams',
                { field: 'projectId', value: this.id },
                async (params) => {
                    const ls = params.docChanges().map(async (itm: any) => {
                        const d = itm.doc.data();
                        switch (itm.type) {
                            case 'added':
                                const pm = this.createProjectMember(d);
                                // await pm.initLocalCache()
                                await pm.listenCloud();
                                break;
                            case 'modified':
                                this.runModification(d);
                                break;
                            case 'removed':
                                this.runRemove(d);
                                break;
                        }
                    });
                    await Promise.all(ls);
                    this.coords.display$.markForCheck();
                },
            );
            uns && (await this.coords.user$.addQuerySubscription('project-team', uns));
        } catch (e) {
            console.error('LISTEN PROJECT TEAM FAILURE', e);
        }
    }

    get disabledCount() {
        let c = 0;
        this._memberMap.forEach((m) => !m.isOn && c++);
        return c;
    }

    async tryLoad() {
        if (this.coords.project$.isDisplayOn) return;

        for (const m of this._memberMap.values()) {
            if (!m.isReady) return;
        }

        await this._load();
        this.isReady = true;
    }

    get currentUser() {
        if (!this._user)
            this._user = Array.from(this._memberMap.values()).find((m) => m.isCurrentUser);
        return this._user!;
    }

    get owner() {
        if (!this._owner) this._owner = Array.from(this._memberMap.values()).find((m) => m.isOwner);
        return this._owner!;
    }

    get currentRole() {
        return this.currentUser?.role || 'owner';
    }

    async runModification(params: any) {
        console.log('runModification', params);
        const pm = this._memberMap.get(params.userId);
        const pil = this.coords.project$.getProjectInList(this.coords.project$.currentId!)!;

        if (!pm) return;
        if (pm.role !== 'owner' && pm.role !== params.role) {
            console.log('ROLE CHANGE', pm.role, params.role);
            pm.role = params.role;
            if (pm.isCurrentUser) pil.role = pm.role;
        }
        pil.isPending = params.isPending;
        pm.isPending = params.isPending;
        this._processOptions(pm, params);
        this.coords.display$.markForCheck();
    }

    async runRemove(params: any) {
        if (params.userId === this.coords.user$.currentId) return location.reload();
        const pm = this._memberMap.get(params.userId);
        if (!pm) return;
        this._memberMap.delete(pm.id);
        this.coords.display$.markForCheck();
    }

    private async _load() {
        console.log('LOAD PROJECT TEAM', this.id);

        let pr: Project;

        const ls = Array.from(this._memberMap.values());
        ls.sort((a, b) => {
            if (a.role === 'owner') return -1;
            if (b.role === 'owner') return 1;
            return 0;
        });

        for (const pm of ls) {
            const p = await pm.load3();
            if (p) pr = p;
        }
        // console.log("CREATED PROJECT", pr!)

        if (!pr!) return;
        pr.team = this;
        await this.coords.project$.setCurrent(pr);
        await this.refreshOther();
        // this.coords.dataset$.main.requestReduplicate()
        console.log('PROJECT TEAM LOADED', this.id);
        const pil = this.coords.project$.getProjectInList(this.id);
        if (pil?.skipAutoOpen) {
            pil.skipAutoOpen = false;
        } else {
            this.coords.project$.isDisplayOn = true;
        }
    }

    async refreshOther() {
        this._memberMap.forEach(async (pm) => await pm.refreshOther());
    }

    get message() {
        const ls: string[] = [];
        this._memberMap.forEach((pm) => {
            ls.push(pm.message);
        });
        return ls.join('');
    }

    createProjectMember(params: {
        userId: string;
        role: ROLE;
        isPending?: boolean;
        options?: any;
    }) {
        const pm = new ProjectMember(this, params.userId, this.id, params.role, !!params.isPending);
        this._memberMap.set(pm.id, pm);
        this._processOptions(pm, params);

        return pm;
    }

    private _processOptions(pm: ProjectMember, params: any) {
        if (params && params.options) {
            if (params.options.canAttach !== undefined)
                pm.setOption('canAttach', params.options.canAttach);
        }
    }

    getMember(memberId: string): ProjectMember | undefined {
        return this._memberMap.get(memberId);
    }

    removeMember(pm: ProjectMember) {
        this._memberMap.delete(pm.id);
    }

    get memberLs() {
        return Array.from(this._memberMap.values()).filter((m) => !m.isPending);
    }

    get invitedLs() {
        return Array.from(this._memberMap.values()).filter((m) => m.isPending);
    }

    activeIdSet(btn: BinaryTreeNode) {
        const s = new Set<string>();
        this._memberMap.forEach((pm) => pm.activeIdSet(btn, s));
        return s;
    }

    otherBt(
        itmId: string,
        bt: BinaryTree,
    ): {
        pm: ProjectMember;
        nodeSet: Set<string>;
    }[] {
        const ls: {
            pm: ProjectMember;
            nodeSet: Set<string>;
        }[] = [];

        this._memberMap.forEach((pm) => {
            const x = pm.otherBt(itmId, bt);
            x && ls.push(x);
        });
        return ls;
    }

    async terminate() {
        this._memberMap.clear();
        await this.coords.user$.closeQuerySubscription('project-team');
    }
}
